import React, { useState } from 'react';
import { ChevronRight, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import RequestForm from './services/RequestForm';

const months = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

interface Category {
  name: string;
  baseRates: {
    fast: number;
    normal: number;
    slow: number;
  };
}

const categories: Category[] = [
  {
    name: 'Одежда',
    baseRates: { fast: 3.0, normal: 2.6, slow: 2.2 }
  },
  {
    name: 'Хоз. товары',
    baseRates: { fast: 2.4, normal: 1.5, slow: 1.2 }
  },
  {
    name: 'Игрушки',
    baseRates: { fast: 1.7, normal: 1.5, slow: 1.2 }
  },
  {
    name: 'Нижнее белье',
    baseRates: { fast: 2.0, normal: 1.7, slow: 0.9 }
  },
  {
    name: 'Инструменты',
    baseRates: { fast: 1.3, normal: 1.2, slow: 1.1 }
  }
];

function PricingPage() {
  const [showForm, setShowForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);

  const generateMonthlyData = (baseRates: Category['baseRates']) => {
    return months.map(month => ({
      month,
      fast: baseRates.fast + (Math.random() * 0.2 - 0.1),
      normal: baseRates.normal + (Math.random() * 0.2 - 0.1),
      slow: baseRates.slow + (Math.random() * 0.2 - 0.1)
    }));
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
          <p className="text-sm text-[#FF3B30]">
            Быстрое авто: ${payload[0].value.toFixed(2)}
          </p>
          <p className="text-sm text-[#34C759]">
            Обычное авто: ${payload[1].value.toFixed(2)}
          </p>
          <p className="text-sm text-[#007AFF]">
            Медленное авто: ${payload[2].value.toFixed(2)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3BA0]/50 via-[#1B3BA0]/30 to-transparent mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Почему нельзя указать точную цену доставки?
            </h1>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Стоимость доставки из Китая варьируется от месяца к месяцу и зависит от объёма, типа товара, 
              сезона и выбранного способа доставки.
            </p>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Чтобы вы понимали ориентиры и динамику цен — мы подготовили наглядный график. А для получения 
              точной стоимости под ваш заказ — просто оставьте заявку, и наш менеджер пришлёт вам коммерческое 
              предложение в течение 5 минут.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center bg-white text-[#1B3BA0] px-8 py-4 rounded-full hover:bg-[#99BBFF] transition-all font-medium text-lg group shadow-lg hover:shadow-xl"
            >
              Получить расчёт
              <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Search and Categories */}
      <section className="py-12 px-6 bg-[#f5f5f7]">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-3xl font-bold text-[#1B3BA0] mb-8 text-center">
            Динамика цен на доставку в 2024 году
          </h2>
          
          {/* Category Selection */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  selectedCategory.name === cat.name
                    ? 'bg-[#1B3BA0] text-white'
                    : 'bg-white text-[#1d1d1f] hover:bg-gray-50'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Price Info */}
          <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-[#1B3BA0]" />
              <p className="text-gray-600">
                График показывает колебания цен на доставку в течение года с учетом сезонности
              </p>
            </div>
            
            {/* Chart */}
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={generateMonthlyData(selectedCategory.baseRates)}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" />
                  <YAxis
                    domain={['dataMin - 0.1', 'dataMax + 0.1']}
                    tickFormatter={(value) => `$${value.toFixed(1)}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="fast"
                    name="Быстрое авто (10-15 дней)"
                    stroke="#FF3B30"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="normal"
                    name="Обычное авто (15-20 дней)"
                    stroke="#34C759"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="slow"
                    name="Медленное авто (18-25 дней)"
                    stroke="#007AFF"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className="mt-6 text-sm text-gray-500 text-center">
              * Цены указаны за 1 кг груза и могут варьироваться в зависимости от объема и сезона
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Получить расчёт стоимости
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Заполните форму, и мы свяжемся с вами для обсуждения деталей
          </p>
          <RequestForm source="Цены" />
        </div>
      </section>

      {/* Request Form Popup */}
      {showForm && <RequestForm isPopup onClose={() => setShowForm(false)} source="Цены" />}
    </div>
  );
}

export default PricingPage;